import { apiRequestWithConfig } from '@lib/api-fetch';
import usioApi from '@lib/api-fetch/usio-api';
import { EmailProvider } from 'types/send-email';
import { FiservCheckoutReq } from 'types/checkout';
import { UsioCardholder } from 'types/usioProfile';
import { SetRecipientsRequest } from 'types/setRecipientType';
import { ClientSafeConfig } from '@lib/context/config-context';
import { fetch_timeout_duration } from '@lib/constants/network-constants';
import { getRuntimeConfig } from '@lib/util/runtime-config-util';
import { getLogger } from '@lib/util/logging-util';

const logger = getLogger();

export async function getGiftCardsByOrder(order_id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/order/gift-cards/${order_id}`,
        {},
        '',
        true,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function getGiftCardByProvisionedCardId(
    provisioned_card_id: string,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    let response: any;
    logger.debug('apiRequest getGiftCardByProvisionedCardId');
    try {
        response = await apiRequestWithConfig(
            'GET',
            `/order/gift-card/${provisioned_card_id}`,
            {},
            '',
            false,
            '',
            fetch_timeout_duration,
            false,
            true,
            runtimeConfig
        );
        logger.debug(response);
    } catch (error) {
        logger.error(error);
        throw error;
    }

    return response.body;
}

export async function getRedemptionTransactionDetails(
    id: string,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const auth_token = sessionStorage.getItem(`auth_token_${id}`) || '';
    const response = await apiRequestWithConfig(
        'GET',
        `/redemption/${id}`,
        {},
        auth_token,
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function getDeliveryInfo(id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/redemption/delivery-info/${id}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function getRedemptionCardInfo(id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const auth_token = sessionStorage.getItem(`auth_token_${id}`) || '';
    const response = await apiRequestWithConfig(
        'GET',
        `/redemption/widget/${id}`,
        {},
        auth_token,
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function redemptionVerify(params: {
    provisioned_card_id?: string;
    email_provider?: EmailProvider;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/otp/initiate`,
        {
            body: {
                ...params,
                email_provider: params.email_provider ?? EmailProvider.SendGrid,
            },
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function getSmsStatus(sid: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/otp/getstatus/${sid}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function getEmailStatus(
    sid: string,
    emailProvider: EmailProvider = EmailProvider.SendGrid,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/otp/getemailstatus/${sid}?email_provider=${emailProvider}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function redemptionConfirmVerify(params: {
    provisioned_card_id: string;
    otp: string;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/otp/validate`,
        {
            body: params,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function smsRedemption(provisioned_card_id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/redemption/sms-redemption/${provisioned_card_id}`,
        { body: {} },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function sendButtonTapped(
    provisioned_card_id: string,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'PUT',
        `/redemption/send-button-tapped/${provisioned_card_id}`,
        { body: {} },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function giftCardChangeBusiness(params: {
    gift_card_id: string;
    search_provider: string;
    business_id: string;
    business_name: string;
    photo_img_url: string;
    rating: number;
    review_count: number;
    provisioned_card_id?: string | null;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'PUT',
        `/order/gift-card/change-business`,
        {
            body: params,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function trackingSendPrepaidGiftCard(
    provisioned_card_id?: string | null,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    try {
        const response = await apiRequestWithConfig(
            'PUT',
            `/order/gift-card/tracking-send-prepaid-gift-card/${provisioned_card_id}`,
            {},
            '',
            false,
            '',
            fetch_timeout_duration,
            false,
            true,
            runtimeConfig
        );
        return response;
    } catch {
        logger.error('trackingSendPrepaidGiftCard - error');
    }
}

export async function trackingForMarketing(order_id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'PUT',
        `/customer/promotional-emails/${order_id}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function fiservAuthorizeSession(config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/fiserv/authorize-session`,
        {
            body: {},
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function fiservCheckout(request: FiservCheckoutReq, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/fiserv/checkout`,
        {
            body: request,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function usioGetCardNumber(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/number`, {}, auth_token, username);
}

export async function usioGetCardCvv(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/cvv`, {}, auth_token, username);
}

export const getCardholderProfile = (auth_token: string) => {
    return usioApi('GET', `/cardholder/profile`, {}, auth_token);
};

export const usioUpdatePhoneNumber = (
    req: UsioCardholder,
    auth_token: string
): Promise<any> => {
    return usioApi('PUT', `/cardholder/profile`, { body: req }, auth_token);
};

export async function usioGetCardBalance(
    auth_token: string,
    username: string
): Promise<any> {
    return await usioApi('GET', `/card/balance`, {}, auth_token, username);
}

export async function validateBuyer(request: {
    cart_id: string;
    email: string;
    phone: string;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/checkout/validate-buyer`,
        {
            body: request,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function validateLogin(request: {
    email: string;
    password: string;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/custom-auth/validate-login`,
        {
            body: request,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function generate2FA(request: {
    customer_id: string;
    email_provider?: EmailProvider;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/custom-auth/generate-2fa`,
        {
            body: request,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function verify2FA(request: {
    customer_id: string;
    code: string;
}, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/custom-auth/verify-2fa`,
        {
            body: request,
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

export async function getPaymentSessionStatus(cart_id: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/payment-session/status/${cart_id}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response;
}

/**
 * Verify reCAPTCHA token with runtime config
 * @param token - reCAPTCHA token
 * @param action - Expected action name
 * @param config - Runtime configuration (optional, auto-fetched from window)
 */
export async function verifyRecaptchaTokenWithConfig(
    token: string,
    action: string,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);

    const response = await apiRequestWithConfig(
        'POST',
        `/recaptcha/verify-checkout-token`,
        {
            body: {
                event: {
                    token: token,
                    expectedAction: action,
                    siteKey: runtimeConfig?.GOOGLE_CAPTCHA_KEY,
                },
            },
        },
        undefined,
        false,
        undefined,
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );

    return response;
}

/**
 * @deprecated Use verifyRecaptchaTokenWithConfig with useApi() hook instead
 */
export async function verifyRecaptchaToken(
    token: string,
    action: string,
    config?: ClientSafeConfig
): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'POST',
        `/recaptcha/verify-checkout-token`,
        {
            body: {
                event: {
                    token: token,
                    expectedAction: action,
                    siteKey: process.env.GOOGLE_CAPTCHA_KEY,
                },
            },
        },
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );

    return response;
}

export async function checkAfterHoursProcessing(
    provisioned_card_id: string,
    config?: ClientSafeConfig
): Promise<boolean> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const timezoneOffset = new Date().getTimezoneOffset();
    const response = await apiRequestWithConfig(
        'GET',
        `/redemption/check-after-hours-processing/${provisioned_card_id}?timezoneOffset=${timezoneOffset}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function getSharePercent(token: string, config?: ClientSafeConfig): Promise<any> {
    // Auto-fetch config from window if not provided
    const runtimeConfig = getRuntimeConfig(config);
    const response = await apiRequestWithConfig(
        'GET',
        `/checkout/share-percent?token=${token}`,
        {},
        '',
        false,
        '',
        fetch_timeout_duration,
        false,
        true,
        runtimeConfig
    );
    return response.body;
}

export async function setRecipientsAsync(req: SetRecipientsRequest, config?: ClientSafeConfig): Promise<any> {
  // Auto-fetch config from window if not provided
  const runtimeConfig = getRuntimeConfig(config);

  const response = await apiRequestWithConfig(
    'POST',
    `/checkout/set-recipients`,
    {
      body: req
    },
    '',
    false,
    '',
    fetch_timeout_duration,
    false,
    true,
    runtimeConfig
  );

  return response;
}